import type { CSSProperties, FC, ReactElement } from "react"

import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"

import { horizontalProgressBarConfig as config } from "./horizontal-progress-bar.config"
import { EHorizontalProgressBarVariant as EVariant } from "./horizontal-progress-bar.types"

type TProps = {
    progress?: number
    variant?: EVariant
    isActive?: boolean
    isParentNotAccented?: boolean
    withoutAnimation?: boolean
    wrapperClassName?: string
    height?: CSSProperties["height"]
    width?: CSSProperties["width"]
}

/**
 * Horizontal Progress bar.
 * Design: {@link https://www.figma.com/design/J6qF3Pa6zeK3udtVWLskjd/Participant?node-id=12490-69424&t=3LDini0nU7NuAOh1-4}
 *
 * @param variant either light or blue
 * @param progress 0-100 value for indication
 * @param isActive indicates whether item as under mouse or keyboard action
 * @param isParentNotAccented helper method to specify whiter background for main line
 * @param withoutAnimation marks being progress bar as non-interactive
 * @param height marks the element height
 * @param width marks the element width
 * @param wrapperClassName custom css to override
 * @return ReactElement
 */
const Component: FC<TProps> = ({
    variant = EVariant.Light,
    progress = config.HORIZONTAL_BAR_MINIMUM_VALUE,
    isActive = false,
    isParentNotAccented = false,
    withoutAnimation = false,
    height = "8px",
    width = "100%",
    wrapperClassName = String()
}: TProps): ReactElement => (
    <div
        style={{ width, height }}
        className={buildCssClassName("rounded-[10px]", !withoutAnimation && getTransitionTwClasses("all", 200), {
            [config.mainPartBgColorMap[variant](isActive, isParentNotAccented)]: variant,
            [wrapperClassName]: wrapperClassName
        })}
    >
        <div
            className={buildCssClassName(
                "h-full rounded-tl-[10px] rounded-bl-[10px]",
                progress === 100 && "rounded-tr-[10px] rounded-br-[10px]",
                !withoutAnimation && getTransitionTwClasses("all", 200),
                {
                    [config.progressPartBgColorMap[variant](isActive)]: variant
                }
            )}
            style={{ width: `${progress}%` }}
        />
    </div>
)

Component.displayName = "HorizontalProgressBar"

export { Component as HorizontalProgressBar }
